import type {
  ILanguage,
  SQDCActionStatus,
  SQDCEventActionTypeInternalType,
  SQDCWorkflowEndBehavior as GraphQLSQDCWorkflowEndBehavior,
  SQDCWorkflowHistoryType as GraphQLSQDCWorkflowHistoryType,
  SQDCWorkflowInstanceStatus as GraphQLSQDCWorkflowInstanceStatus,
  SQDCWorkflowInstanceStepStatus as GraphQLSQDCWorkflowInstanceStepStatus,
  SQDCWorkflowNodeTransfer as GraphQLSQDCWorkflowNodeTransfer,
  SQDCWorkflowFlagMode as GraphQLSQDCWorkflowFlagMode,
  SQDCWorkflowNodeType as GraphQLSQDCWorkflowNodeType,
  SQDCWorkflowParallelBranchStatus as GraphQLSQDCWorkflowParallelBranchStatus,
  SQDCWorkflowParallelExecutionStatus as GraphQLSQDCWorkflowParallelExecutionStatus,
  SQDCWorkflowStartItemType as GraphQLSQDCWorkflowStartItemType,
} from "@/graphql";

export type SQDCWorkflowNodeType = `${GraphQLSQDCWorkflowNodeType}`;
export type SQDCWorkflowStartItemType = `${GraphQLSQDCWorkflowStartItemType}`;
export type SQDCWorkflowEndBehavior = `${GraphQLSQDCWorkflowEndBehavior}`;
export type SQDCWorkflowNodeTransfer = `${GraphQLSQDCWorkflowNodeTransfer}`;
export type SQDCWorkflowFlagMode = `${GraphQLSQDCWorkflowFlagMode}`;
export type SQDCWorkflowStatus = `${GraphQLSQDCWorkflowInstanceStatus}`;
export type SQDCWorkflowStepStatus = `${GraphQLSQDCWorkflowInstanceStepStatus}`;
export type SQDCWorkflowParallelExecutionStatus = `${GraphQLSQDCWorkflowParallelExecutionStatus}`;
export type SQDCWorkflowParallelBranchStatus = `${GraphQLSQDCWorkflowParallelBranchStatus}`;
export type SQDCWorkflowLocalizedText = Partial<ILanguage>;

export type SQDCWorkflowPosition = {
  x: number;
  y: number;
};

export type SQDCWorkflowNode = {
  id: string;
  type: SQDCWorkflowNodeType;
  position: SQDCWorkflowPosition;
  startItemType?: SQDCWorkflowStartItemType;
  eventActionTypeId?: number;
  shorthandId?: number;
  transfer: SQDCWorkflowNodeTransfer;
  delegateBoardId?: number;
  parentParallelNodeId?: string;
  endBehavior?: SQDCWorkflowEndBehavior;
  apiUrl?: string;
  apiFetchOptions?: string;
  variableBindings?: SQDCWorkflowNodeVariableBinding[];
};

export type SQDCWorkflowNodeVariableBinding = {
  id: string;
  variableId: string;
  fieldId: number;
  readonly: boolean;
  read: boolean;
  write: boolean;
};

export type SQDCWorkflowVariable = {
  id: string;
  key: string;
  type: "TEXT";
};

export type SQDCWorkflowConnection = {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  scopeParallelNodeId?: string;
  sortOrder: number;
  previousActionStatus?: SQDCActionStatus;
  archiveEvent?: boolean;
  requiresComment?: boolean;
  datapointTypeId?: number;
  flagMode?: SQDCWorkflowFlagMode;
  flagIds?: number[];
  descriptionId?: number;
  description?: SQDCWorkflowLocalizedText;
  showDescriptionAsTooltip?: boolean;
  autoProceed?: boolean;
  routingPoints?: SQDCWorkflowPosition[];
  buttonLabelId?: number;
  buttonLabel?: SQDCWorkflowLocalizedText;
};

export function requiresSQDCWorkflowProceed(
  transitions: readonly SQDCWorkflowConnection[],
  canAdvance: boolean,
  startConnection?: SQDCWorkflowConnection,
) {
  return (
    startConnection?.autoProceed === true ||
    (canAdvance && transitions.some((transition) => transition.autoProceed === true))
  );
}

export type SQDCWorkflowDefinition = {
  id: string;
  ready: boolean;
  usageCount: number;
  nameId?: number;
  name: SQDCWorkflowLocalizedText;
  description: string;
  icon?: string | null;
  internalType?: SQDCEventActionTypeInternalType | null;
  internalTypeSiteId?: number | null;
  siteIds: number[];
  boardIds: number[];
  nodes: SQDCWorkflowNode[];
  connections: SQDCWorkflowConnection[];
  variables?: SQDCWorkflowVariable[];
  createdAt: string;
  updatedAt: string;
};

export type SQDCWorkflowStep = {
  workflowStepId: string;
  status: SQDCWorkflowStepStatus;
  completedAt?: string;
  completedConnectionId?: string;
  completedButtonLabel?: SQDCWorkflowLocalizedText;
  completedDescription?: SQDCWorkflowLocalizedText;
  completedDescriptionShownAsTooltip?: boolean;
  parallelBranchId?: string;
  node: SQDCWorkflowNode;
  eventId?: number;
  actionId?: number;
  responsibleUserId?: number;
  createdAt: string;
};

export type SQDCWorkflowHistoryEntry = {
  id: string;
  at: string;
  type: `${GraphQLSQDCWorkflowHistoryType}`;
  stepId?: string;
  label: string;
  completedConnectionId?: string;
  completedButtonLabel?: SQDCWorkflowLocalizedText;
  completedDescription?: SQDCWorkflowLocalizedText;
  completedDescriptionShownAsTooltip?: boolean;
};

export type SQDCWorkflowParallelBranch = {
  id: string;
  parallelExecutionId: string;
  entryNodeId: string;
  status: SQDCWorkflowParallelBranchStatus;
  completedAt?: string;
};

export type SQDCWorkflowParallelExecution = {
  id: string;
  parallelNodeId: string;
  parentBranchId?: string;
  status: SQDCWorkflowParallelExecutionStatus;
  completedAt?: string;
  branches: SQDCWorkflowParallelBranch[];
};

export type SQDCWorkflowInstance = {
  workflowInstanceId: string;
  definitionId: string;
  definitionNameId?: number;
  definitionName: SQDCWorkflowLocalizedText;
  status: SQDCWorkflowStatus;
  steps: SQDCWorkflowStep[];
  eventIds: number[];
  actionIds: number[];
  variableValues?: Record<string, string | undefined>;
  history: SQDCWorkflowHistoryEntry[];
  parallelExecutions: SQDCWorkflowParallelExecution[];
  createdAt: string;
  updatedAt: string;
};

export function getActiveSQDCWorkflowSteps(instance: SQDCWorkflowInstance) {
  if (instance.status !== "ACTIVE") return [];
  const activeSteps = instance.steps.filter((step) => step.status === "ACTIVE");
  const ownership = new Set<string>();
  for (const step of activeSteps) {
    const owner = step.parallelBranchId ?? "root";
    if (ownership.has(owner)) {
      throw new Error(`Workflow execution owner ${owner} has more than one active step`);
    }
    ownership.add(owner);
    const itemBound =
      (step.node.type === "EVENT" && step.eventId != null && step.actionId == null) ||
      (step.node.type === "ACTION" && step.actionId != null && step.eventId == null);
    if (!itemBound) {
      throw new Error(`Active workflow step ${step.workflowStepId} is not item-bound`);
    }
  }
  return activeSteps;
}

export function getActiveSQDCWorkflowStep(instance: SQDCWorkflowInstance) {
  const activeSteps = getActiveSQDCWorkflowSteps(instance);
  if (instance.status !== "ACTIVE") return undefined;
  if (activeSteps.some((step) => step.parallelBranchId != null)) {
    throw new Error("Use getActiveSQDCWorkflowSteps for a Parallel workflow instance");
  }
  if (activeSteps.length !== 1) {
    throw new Error(
      `Active linear workflow instance requires exactly one active root step; found ${activeSteps.length}`,
    );
  }
  return activeSteps[0];
}

export function getSQDCWorkflowItemContext(
  instances: readonly SQDCWorkflowInstance[],
  item: { eventId: number } | { actionId: number },
) {
  const matchesItem = (step: SQDCWorkflowStep) =>
    "eventId" in item ? step.eventId === item.eventId : step.actionId === item.actionId;
  const byLatestStep = (
    left: { instance: SQDCWorkflowInstance; step: SQDCWorkflowStep },
    right: { instance: SQDCWorkflowInstance; step: SQDCWorkflowStep },
  ) =>
    Date.parse(right.step.completedAt ?? right.step.createdAt) -
      Date.parse(left.step.completedAt ?? left.step.createdAt) ||
    Date.parse(right.instance.updatedAt) - Date.parse(left.instance.updatedAt);
  const contexts = instances.flatMap((instance) =>
    instance.steps
      .filter(matchesItem)
      .map((step) => ({ instance, step }))
      .sort(byLatestStep),
  );
  const activeContext = contexts
    .filter(({ instance, step }) =>
      getActiveSQDCWorkflowSteps(instance).some(
        (activeStep) => activeStep.workflowStepId === step.workflowStepId,
      ),
    )
    .sort(byLatestStep)[0];

  return (
    activeContext ??
    contexts.filter(({ step }) => step.status === "COMPLETED").sort(byLatestStep)[0]
  );
}

export function resolveSQDCWorkflowParallelJoinTarget(
  instance: SQDCWorkflowInstance,
  definition: SQDCWorkflowDefinition,
  parallelBranchId: string,
) {
  let completingBranchId: string | undefined = parallelBranchId;
  const visitedExecutions = new Set<string>();
  while (completingBranchId != null) {
    const execution = instance.parallelExecutions.find((candidate) =>
      candidate.branches.some((branch) => branch.id === completingBranchId),
    );
    if (execution == null || visitedExecutions.has(execution.id)) return undefined;
    visitedExecutions.add(execution.id);
    const hasOtherUnfinishedBranch = execution.branches.some(
      (branch) => branch.id !== completingBranchId && branch.status !== "COMPLETED",
    );
    if (hasOtherUnfinishedBranch) return undefined;

    const parallelNode = definition.nodes.find(({ id }) => id === execution.parallelNodeId);
    const outgoing = definition.connections.filter(
      (connection) =>
        connection.sourceNodeId === execution.parallelNodeId &&
        (connection.scopeParallelNodeId ?? null) === (parallelNode?.parentParallelNodeId ?? null),
    );
    if (outgoing.length !== 1) return undefined;
    const target = definition.nodes.find((node) => node.id === outgoing[0].targetNodeId);
    if (target == null) return undefined;
    if (execution.parentBranchId == null || target.type !== "END") return target;
    completingBranchId = execution.parentBranchId;
  }
  return undefined;
}

export function resolveSQDCWorkflowParallelContinuationTarget(
  instance: SQDCWorkflowInstance,
  definition: SQDCWorkflowDefinition,
  parallelBranchId: string,
) {
  let branchId: string | undefined = parallelBranchId;
  const visitedExecutions = new Set<string>();
  while (branchId != null) {
    const execution = instance.parallelExecutions.find((candidate) =>
      candidate.branches.some((branch) => branch.id === branchId),
    );
    if (execution == null || visitedExecutions.has(execution.id)) return undefined;
    visitedExecutions.add(execution.id);

    const parallelNode = definition.nodes.find(({ id }) => id === execution.parallelNodeId);
    const outgoing = definition.connections.filter(
      (connection) =>
        connection.sourceNodeId === execution.parallelNodeId &&
        (connection.scopeParallelNodeId ?? null) === (parallelNode?.parentParallelNodeId ?? null),
    );
    if (outgoing.length !== 1) return undefined;
    const target = definition.nodes.find((node) => node.id === outgoing[0].targetNodeId);
    if (target == null) return undefined;
    if (execution.parentBranchId == null || target.type !== "END") return target;
    branchId = execution.parentBranchId;
  }
  return undefined;
}

export type SQDCWorkflowValidationError = {
  nodeId?: string;
  connectionId?: string;
  message: string;
  params?: Record<string, string>;
};

export function createSQDCWorkflowDefinition(): SQDCWorkflowDefinition {
  const now = new Date().toISOString();

  return {
    id: crypto.randomUUID(),
    ready: false,
    usageCount: 0,
    name: {},
    description: "",
    icon: undefined,
    siteIds: [],
    boardIds: [],
    nodes: [],
    connections: [],
    variables: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function ensureSQDCWorkflowStartNode(
  workflow: SQDCWorkflowDefinition,
): SQDCWorkflowDefinition {
  const normalizedWorkflow = {
    ...workflow,
    nodes: workflow.nodes.map((node) => ({
      ...node,
      transfer: node.transfer ?? "NONE",
      parentParallelNodeId: node.parentParallelNodeId ?? undefined,
      variableBindings: node.variableBindings ?? [],
    })),
    connections: normalizeSQDCWorkflowConnectionOrder(workflow.connections).map((connection) => ({
      ...connection,
      flagIds: connection.flagIds ?? [],
      routingPoints: connection.routingPoints ?? [],
      buttonLabel: normalizeSQDCWorkflowLocalizedText(connection.buttonLabel),
    })),
  };
  return normalizedWorkflow;
}

export function collapseSQDCWorkflowInternalEnds(
  workflow: SQDCWorkflowDefinition,
): SQDCWorkflowDefinition {
  return workflow;
}

export function materializeSQDCWorkflowInternalEnds(
  workflow: SQDCWorkflowDefinition,
): SQDCWorkflowDefinition {
  return workflow;
}

export function normalizeSQDCWorkflowLocalizedText(
  value: SQDCWorkflowLocalizedText | string | null | undefined,
): SQDCWorkflowLocalizedText {
  if (typeof value === "string") return { de: value, en: value };
  if (value == null || typeof value !== "object") return {};
  return Object.fromEntries(
    Object.entries(value).filter(([, translation]) => typeof translation === "string"),
  );
}

export function normalizeSQDCWorkflowConnectionOrder(
  connections: SQDCWorkflowConnection[],
): SQDCWorkflowConnection[] {
  const orderByConnectionId = new Map<string, number>();
  const connectionsBySource = new Map<string, SQDCWorkflowConnection[]>();
  connections.forEach((connection) => {
    const key = `${connection.sourceNodeId}:${connection.scopeParallelNodeId ?? "root"}`;
    const outgoingConnections = connectionsBySource.get(key) ?? [];
    outgoingConnections.push(connection);
    connectionsBySource.set(key, outgoingConnections);
  });
  connectionsBySource.forEach((outgoingConnections) => {
    outgoingConnections
      .map((connection, index) => ({ connection, index }))
      .sort(
        (left, right) =>
          (left.connection.sortOrder ?? left.index) - (right.connection.sortOrder ?? right.index) ||
          left.index - right.index,
      )
      .forEach(({ connection }, index) => orderByConnectionId.set(connection.id, index));
  });
  return connections.map((connection) => ({
    ...connection,
    sortOrder: orderByConnectionId.get(connection.id) ?? 0,
    archiveEvent: connection.archiveEvent ?? false,
    requiresComment: connection.requiresComment ?? false,
    showDescriptionAsTooltip: connection.showDescriptionAsTooltip ?? false,
    autoProceed: connection.autoProceed ?? false,
    buttonLabel: connection.buttonLabel ?? {},
    description: connection.description ?? {},
  }));
}

export function validateSQDCWorkflow(
  workflow: SQDCWorkflowDefinition,
  locale: string,
  translate?: (key: string) => string,
): SQDCWorkflowValidationError[] {
  const errors: SQDCWorkflowValidationError[] = [];
  const resolveText = (value: SQDCWorkflowLocalizedText | string | undefined) =>
    resolveSQDCWorkflowText(value, locale, translate);
  const rootNodes = workflow.nodes.filter((node) => node.parentParallelNodeId == null);
  const startNodes = rootNodes.filter((node) => node.type === "START");
  const endNodes = rootNodes.filter((node) => node.type === "END");

  if (
    !Object.values(normalizeSQDCWorkflowLocalizedText(workflow.name)).some(
      (name) => typeof name === "string" && name.trim() !== "",
    )
  ) {
    errors.push({
      message: "validation.required",
      params: {
        field:
          translate?.("modules.sqdc.components.workflow.SQDCWorkflowEditor.workflowName") ??
          "Workflow name",
      },
    });
  }

  if (startNodes.length < 1 || startNodes.some(({ startItemType }) => startItemType == null)) {
    errors.push({
      message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.start",
    });
  }
  if (endNodes.length === 0) {
    errors.push({
      message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.end",
    });
  }

  workflow.nodes.forEach((node) => {
    const hasIncoming =
      node.type === "START" ||
      workflow.connections.some((connection) => connection.targetNodeId === node.id);
    const hasOutgoing =
      node.type === "END" ||
      workflow.connections.some((connection) => connection.sourceNodeId === node.id);
    if (!hasIncoming || !hasOutgoing) {
      errors.push({
        nodeId: node.id,
        message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.notConnected",
        params: { name: node.type },
      });
    }
    if (
      node.type === "END" &&
      workflow.connections.some((connection) => connection.sourceNodeId === node.id)
    ) {
      errors.push({
        nodeId: node.id,
        message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.endOutgoing",
        params: { name: node.type },
      });
    }
    if (
      node.type === "START" &&
      workflow.connections.filter((connection) => connection.sourceNodeId === node.id).length !== 1
    ) {
      errors.push({
        nodeId: node.id,
        message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.startOutgoing",
      });
    }
    if (
      node.type === "START" &&
      workflow.connections.some((connection) => connection.targetNodeId === node.id)
    ) {
      errors.push({
        nodeId: node.id,
        message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.startIncoming",
      });
    }
  });

  startNodes.forEach((startNode) => {
    const connection = workflow.connections.find(
      ({ sourceNodeId }) => sourceNodeId === startNode.id,
    );
    const target = workflow.nodes.find(({ id }) => id === connection?.targetNodeId);
    if (target == null || target.type !== startNode.startItemType) {
      errors.push({
        nodeId: startNode.id,
        message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.configuration",
        params: { name: startNode.startItemType ?? "Start" },
      });
    }
  });

  workflow.connections.forEach((connection) => {
    const source = workflow.nodes.find(({ id }) => id === connection.sourceNodeId);
    if (source == null) return;
    const selectable =
      source.type === "START" || source.type === "EVENT" || source.type === "ACTION";
    const isActionStart = source.type === "START" && source.startItemType === "ACTION";
    const missingButtonLabel = selectable && resolveText(connection.buttonLabel).trim() === "";
    const missingDescription =
      connection.showDescriptionAsTooltip === true &&
      resolveText(connection.description).trim() === "";
    const invalid =
      missingButtonLabel ||
      (connection.requiresComment && connection.autoProceed) ||
      (connection.flagMode == null) !== ((connection.flagIds?.length ?? 0) === 0) ||
      missingDescription ||
      (source.type === "START" &&
        (connection.requiresComment ||
          connection.previousActionStatus != null ||
          (!isActionStart &&
            (connection.datapointTypeId != null ||
              connection.flagMode != null ||
              (connection.flagIds?.length ?? 0) > 0 ||
              connection.archiveEvent)))) ||
      (source.type === "PARALLEL" &&
        (connection.requiresComment ||
          connection.autoProceed ||
          connection.datapointTypeId != null ||
          connection.flagMode != null ||
          (connection.flagIds?.length ?? 0) > 0 ||
          connection.previousActionStatus != null ||
          connection.archiveEvent ||
          connection.showDescriptionAsTooltip));
    if (invalid) {
      errors.push({
        connectionId: connection.id,
        message: missingButtonLabel
          ? "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.connectionLabel"
          : missingDescription
            ? "validation.required"
            : "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.connectionConfiguration",
        params: missingDescription
          ? {
              field:
                translate?.(
                  "modules.sqdc.components.workflow.SQDCWorkflowEditor.connectionDescription",
                ) ?? "Description",
            }
          : { name: resolveText(connection.buttonLabel) },
      });
    }
  });
  new Set(workflow.connections.map(({ sourceNodeId }) => sourceNodeId)).forEach((sourceNodeId) => {
    if (
      workflow.connections.filter(
        (connection) => connection.sourceNodeId === sourceNodeId && connection.autoProceed,
      ).length > 1
    ) {
      errors.push({
        nodeId: sourceNodeId,
        message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.configuration",
        params: { name: "Auto proceed" },
      });
    }
  });
  if (hasSQDCWorkflowAutoProceedCycle(workflow)) {
    errors.push({ message: "api.toast.error.workflowContainsAutoProceedCycle" });
  }

  const rootReachableNodeIds = new Set<string>();
  const visit = (nodeId: string) => {
    if (rootReachableNodeIds.has(nodeId)) return;
    rootReachableNodeIds.add(nodeId);
    workflow.connections
      .filter((connection) => connection.sourceNodeId === nodeId)
      .forEach((connection) => visit(connection.targetNodeId));
  };
  startNodes.forEach(({ id }) => visit(id));
  workflow.nodes
    .filter((node) => node.parentParallelNodeId == null && !rootReachableNodeIds.has(node.id))
    .forEach((node) => {
      errors.push({
        nodeId: node.id,
        message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.notReachable",
        params: { name: node.type },
      });
    });

  workflow.nodes
    .filter((node) => node.type === "EVENT" || node.type === "ACTION")
    .forEach((node) => {
      if (node.eventActionTypeId == null && node.shorthandId == null) {
        errors.push({
          nodeId: node.id,
          message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.configuration",
          params: { name: node.type },
        });
      }
      if (node.transfer === "DELEGATE" && node.delegateBoardId == null) {
        errors.push({
          nodeId: node.id,
          message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.delegateBoard",
          params: { name: node.type },
        });
      }
    });

  errors.push(...validateSQDCWorkflowParallelStructure(workflow, translate));

  return errors;
}

export function getSQDCWorkflowParallelChildren(
  workflow: SQDCWorkflowDefinition,
  parallelNodeId: string,
) {
  return workflow.nodes.filter((node) => node.parentParallelNodeId === parallelNodeId);
}

export function getSQDCWorkflowInternalEntries(
  workflow: SQDCWorkflowDefinition,
  parallelNodeId: string,
) {
  const entryIds = new Set(
    workflow.connections
      .filter(
        (connection) =>
          connection.sourceNodeId === parallelNodeId &&
          connection.scopeParallelNodeId === parallelNodeId,
      )
      .map((connection) => connection.targetNodeId),
  );
  return getSQDCWorkflowParallelChildren(workflow, parallelNodeId).filter((node) =>
    entryIds.has(node.id),
  );
}

export function getSQDCWorkflowInternalExits(
  workflow: SQDCWorkflowDefinition,
  parallelNodeId: string,
) {
  const sourceIds = new Set(
    workflow.connections
      .filter(
        (connection) =>
          connection.targetNodeId === parallelNodeId &&
          connection.scopeParallelNodeId === parallelNodeId,
      )
      .map((connection) => connection.sourceNodeId),
  );
  return getSQDCWorkflowParallelChildren(workflow, parallelNodeId).filter((node) =>
    sourceIds.has(node.id),
  );
}

export function getSQDCWorkflowEntryOwners(
  workflow: SQDCWorkflowDefinition,
  parallelNodeId: string,
) {
  const children = getSQDCWorkflowParallelChildren(workflow, parallelNodeId);
  const childIds = new Set(children.map((node) => node.id));
  const connections = workflow.connections.filter(
    (connection) => childIds.has(connection.sourceNodeId) && childIds.has(connection.targetNodeId),
  );
  const owners = new Map<string, Set<string>>();
  getSQDCWorkflowInternalEntries(workflow, parallelNodeId).forEach((entry) => {
    reachableNodeIds(entry.id, connections).forEach((nodeId) => {
      const entryIds = owners.get(nodeId) ?? new Set<string>();
      entryIds.add(entry.id);
      owners.set(nodeId, entryIds);
    });
  });
  return owners;
}

export function hasSQDCWorkflowParentCycle(workflow: SQDCWorkflowDefinition) {
  const nodeById = new Map(workflow.nodes.map((node) => [node.id, node]));
  const visited = new Set<string>();
  const visiting = new Set<string>();
  const visit = (nodeId: string): boolean => {
    if (visiting.has(nodeId)) return true;
    if (visited.has(nodeId)) return false;
    visiting.add(nodeId);
    const parentId = nodeById.get(nodeId)?.parentParallelNodeId;
    const cyclic = parentId != null && nodeById.has(parentId) && visit(parentId);
    visiting.delete(nodeId);
    visited.add(nodeId);
    return cyclic;
  };
  return workflow.nodes.some((node) => visit(node.id));
}

export function hasSQDCWorkflowInternalCycle(
  workflow: SQDCWorkflowDefinition,
  parallelNodeId: string,
) {
  const children = getSQDCWorkflowParallelChildren(workflow, parallelNodeId);
  const childIds = new Set(children.map((node) => node.id));
  const connections = workflow.connections.filter(
    (connection) => childIds.has(connection.sourceNodeId) && childIds.has(connection.targetNodeId),
  );
  const visited = new Set<string>();
  const visiting = new Set<string>();
  const visit = (nodeId: string): boolean => {
    if (visiting.has(nodeId)) return true;
    if (visited.has(nodeId)) return false;
    visiting.add(nodeId);
    const cyclic = connections
      .filter((connection) => connection.sourceNodeId === nodeId)
      .some((connection) => visit(connection.targetNodeId));
    visiting.delete(nodeId);
    visited.add(nodeId);
    return cyclic;
  };
  return children.some((node) => visit(node.id));
}

export function hasSQDCWorkflowAutoProceedCycle(workflow: SQDCWorkflowDefinition) {
  const nodeById = new Map(workflow.nodes.map((node) => [node.id, node]));
  const structuralTargets = (
    connection: SQDCWorkflowConnection,
    visitedParallelNodeIds = new Set<string>(),
  ): string[] => {
    const target = nodeById.get(connection.targetNodeId);
    if (target?.type !== "PARALLEL") return target == null ? [] : [target.id];
    if (visitedParallelNodeIds.has(target.id)) return [];
    const visited = new Set(visitedParallelNodeIds).add(target.id);
    const isCompletion = connection.scopeParallelNodeId === target.id;
    const continuations = workflow.connections.filter((candidate) =>
      isCompletion
        ? candidate.sourceNodeId === target.id &&
          (candidate.scopeParallelNodeId ?? null) === (target.parentParallelNodeId ?? null)
        : candidate.sourceNodeId === target.id && candidate.scopeParallelNodeId === target.id,
    );
    return continuations.flatMap((candidate) => structuralTargets(candidate, visited));
  };
  const adjacency = new Map<string, string[]>();
  workflow.connections
    .filter(
      (connection) =>
        connection.autoProceed === true &&
        nodeById.get(connection.sourceNodeId)?.type !== "PARALLEL",
    )
    .forEach((connection) => {
      adjacency.set(connection.sourceNodeId, structuralTargets(connection));
    });
  const visited = new Set<string>();
  const visiting = new Set<string>();
  const visit = (nodeId: string): boolean => {
    if (visiting.has(nodeId)) return true;
    if (visited.has(nodeId)) return false;
    visiting.add(nodeId);
    const cyclic = (adjacency.get(nodeId) ?? []).some(visit);
    visiting.delete(nodeId);
    visited.add(nodeId);
    return cyclic;
  };
  return [...adjacency.keys()].some(visit);
}

export function validateSQDCWorkflowParallelStructure(
  workflow: SQDCWorkflowDefinition,
  translate?: (key: string) => string,
): SQDCWorkflowValidationError[] {
  const errors: SQDCWorkflowValidationError[] = [];
  const nodeById = new Map(workflow.nodes.map((node) => [node.id, node]));
  const addNodeError = (node: SQDCWorkflowNode) =>
    errors.push({
      nodeId: node.id,
      message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.notConnected",
      params: {
        name:
          translate?.(
            `modules.sqdc.components.workflow.SQDCWorkflowEditor.nodeTypes.${node.type}`,
          ) || node.type,
      },
    });
  const addConnectionError = (connection: SQDCWorkflowConnection) =>
    errors.push({
      connectionId: connection.id,
      message: "modules.sqdc.components.workflow.SQDCWorkflowEditor.errors.notConnected",
      params: { name: connection.id.slice(0, 8) },
    });

  workflow.nodes.forEach((node) => {
    if (node.parentParallelNodeId == null) return;
    const parent = nodeById.get(node.parentParallelNodeId);
    if (node.type === "START" || node.type === "END" || parent?.type !== "PARALLEL") {
      addNodeError(node);
    }
  });
  if (hasSQDCWorkflowParentCycle(workflow)) {
    workflow.nodes.filter((node) => node.parentParallelNodeId != null).forEach(addNodeError);
  }
  workflow.connections.forEach((connection) => {
    const source = nodeById.get(connection.sourceNodeId);
    const target = nodeById.get(connection.targetNodeId);
    if (source == null || target == null) return addConnectionError(connection);
    const scope = connection.scopeParallelNodeId ?? null;
    const ordinary =
      (source.parentParallelNodeId ?? null) === scope &&
      (target.parentParallelNodeId ?? null) === scope;
    const entry =
      source.type === "PARALLEL" &&
      source.id === scope &&
      target.parentParallelNodeId === source.id;
    const completion =
      target.type === "PARALLEL" &&
      target.id === scope &&
      source.parentParallelNodeId === target.id;
    const outer =
      source.type === "PARALLEL" &&
      (source.parentParallelNodeId ?? null) === scope &&
      (target.parentParallelNodeId ?? null) === scope;
    if (nodeById.get(scope ?? "")?.type !== "PARALLEL" && scope != null) {
      addConnectionError(connection);
    } else if (!ordinary && !entry && !completion && !outer) {
      addConnectionError(connection);
    }
  });

  workflow.nodes
    .filter((node) => node.type === "PARALLEL")
    .forEach((parallelNode) => {
      const entries = workflow.connections.filter(
        (connection) =>
          connection.sourceNodeId === parallelNode.id &&
          connection.scopeParallelNodeId === parallelNode.id,
      );
      const outer = workflow.connections.filter(
        (connection) =>
          connection.sourceNodeId === parallelNode.id &&
          (connection.scopeParallelNodeId ?? null) === (parallelNode.parentParallelNodeId ?? null),
      );
      if (
        entries.length < 2 ||
        outer.length !== 1 ||
        parallelNode.eventActionTypeId != null ||
        parallelNode.shorthandId != null ||
        parallelNode.transfer !== "NONE" ||
        parallelNode.delegateBoardId != null ||
        parallelNode.endBehavior != null
      ) {
        addNodeError(parallelNode);
      }
      const children = getSQDCWorkflowParallelChildren(workflow, parallelNode.id);
      const childIds = new Set(children.map(({ id }) => id));
      if (entries.some(({ targetNodeId }) => !childIds.has(targetNodeId))) {
        addNodeError(parallelNode);
      }
      const scoped = workflow.connections.filter(
        ({ scopeParallelNodeId }) => scopeParallelNodeId === parallelNode.id,
      );
      const adjacency = new Map<string, string[]>();
      scoped.forEach((connection) => {
        if (connection.sourceNodeId === parallelNode.id) return;
        adjacency.set(connection.sourceNodeId, [
          ...(adjacency.get(connection.sourceNodeId) ?? []),
          connection.targetNodeId,
        ]);
      });
      const reachable = new Set<string>();
      const visit = (nodeId: string) => {
        if (nodeId === parallelNode.id || reachable.has(nodeId)) return;
        reachable.add(nodeId);
        (adjacency.get(nodeId) ?? []).forEach(visit);
      };
      entries.forEach(({ targetNodeId }) => visit(targetNodeId));
      children.filter(({ id }) => !reachable.has(id)).forEach(addNodeError);
      const entryOwners = getSQDCWorkflowEntryOwners(workflow, parallelNode.id);
      children.filter(({ id }) => entryOwners.get(id)?.size !== 1).forEach(addNodeError);
      const memo = new Map<string, boolean>();
      const visiting = new Set<string>();
      const reachesBoundary = (nodeId: string): boolean => {
        if (nodeId === parallelNode.id) return true;
        if (visiting.has(nodeId)) return false;
        if (memo.has(nodeId)) return memo.get(nodeId)!;
        visiting.add(nodeId);
        const result = (adjacency.get(nodeId) ?? []).some(reachesBoundary);
        visiting.delete(nodeId);
        memo.set(nodeId, result);
        return result;
      };
      children.filter(({ id }) => !reachesBoundary(id)).forEach(addNodeError);
    });
  return errors;
}

function reachableNodeIds(startNodeId: string, connections: SQDCWorkflowConnection[]) {
  const reachable = new Set<string>();
  const visit = (nodeId: string) => {
    if (reachable.has(nodeId)) return;
    reachable.add(nodeId);
    connections
      .filter((connection) => connection.sourceNodeId === nodeId)
      .forEach((connection) => visit(connection.targetNodeId));
  };
  visit(startNodeId);
  return reachable;
}

export function resolveSQDCWorkflowText(
  value: SQDCWorkflowLocalizedText | string | undefined,
  locale: string,
  translate?: (key: string) => string,
) {
  if (typeof value === "string") return value;
  if (value == null) return "";
  if (value.id != null && translate != null) return translate(`dynamic.${value.id}`);
  const normalizedLocale = locale.replace("-", "_");
  const baseLocale = normalizedLocale.split("_")[0];
  const language = value as Record<string, string | number | null | undefined>;
  return (
    stringValue(language[normalizedLocale]) ??
    stringValue(language[baseLocale]) ??
    stringValue(value.en) ??
    stringValue(value.en_GB) ??
    stringValue(value.de) ??
    Object.values(value).find(
      (text): text is string => typeof text === "string" && text.trim() !== "",
    ) ??
    ""
  );
}

export function resolveSQDCWorkflowStartConnectionLabel(
  connection: SQDCWorkflowConnection,
  locale: string,
  translate?: (key: string) => string,
) {
  const configuredLabel = resolveSQDCWorkflowText(connection.buttonLabel, locale, translate);
  if (configuredLabel !== "") return configuredLabel;
  return connection.buttonLabelId == null || translate == null
    ? ""
    : translate(`dynamic.${connection.buttonLabelId}`);
}

function stringValue(value: string | number | null | undefined) {
  return typeof value === "string" ? value : undefined;
}

export function getSQDCWorkflowStartNodes(workflow: SQDCWorkflowDefinition) {
  return getSQDCWorkflowStartTransitions(workflow).map(({ firstNode }) => firstNode);
}

export function getSQDCWorkflowStartTransitions(workflow: SQDCWorkflowDefinition) {
  return workflow.nodes
    .filter((node) => node.type === "START")
    .flatMap((startNode) => {
      const connection = workflow.connections.find(
        (candidate) => candidate.sourceNodeId === startNode.id,
      );
      const firstNode = workflow.nodes.find((node) => node.id === connection?.targetNodeId);
      return connection == null || firstNode == null ? [] : [{ startNode, connection, firstNode }];
    });
}
